'use client';

import type React from 'react';
import { useRef } from 'react';
import { usePresence } from 'framer-motion';
import { noop } from 'utils/noop';
import { useIsomorphicLayoutEffect } from '../../../hooks/useIsomorphicLayoutEffect';
import { animate } from '../animate';
import { useCustomContext } from '../components/CustomContext';
import { useInitialPresence } from '../components/InitialPresenceContext';
import { setStyles } from '../utils/set-styles';
import type { PresenceAnimation } from './types';
import { useAnimation } from './useAnimation';
import { resolveOptions } from './utils/animation-options';
import { resolveAnimationValues } from './utils/animation-resolver';

/**
 * @deprecated Use framer-motion's `useAnimation` hook instead.
 * Provide mount and unmount animations for react components when
 * used as a descendant of `Presence`.
 *
 * @example
 * usePresenceAnimation({
 *   enter: { opacity: 0 },
 *   target: { opacity: 1 },
 *   exit: { target: 0 },
 *   duration: 0.2
 * });
 *
 * Once the component is mounted, changes to target will result in new animations.
 *
 * usePresenceAnimation({
 *   enter: { opacity: 0 },
 *   target: { opacity: isDisabled ? 0.4 : 1 },
 *   exit: { target: 0 },
 *   duration: 0.2
 * });
 */

export const usePresenceAnimation = (
  ref: React.RefObject<HTMLElement | null>,
  {
    enter,
    target,
    exit,
    onEntered = noop,
    onEntering = noop,
    onExited = noop,
    ...sharedOptions
  }: PresenceAnimation
) => {
  const [isPresent, safeToRemove] = usePresence();
  const shouldSuppressAnimation = useInitialPresence();
  const prevPresent = useRef(null);
  const isUnmountAnimationPlayingRef = useRef(false);

  const custom = useCustomContext();

  useIsomorphicLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    const presenceChanged = isPresent !== prevPresent.current;
    const unmounting = isUnmountAnimationPlayingRef.current;
    // @ts-expect-error FIXME(strictNullChecks)
    prevPresent.current = isPresent;

    if (!presenceChanged) {
      return;
    }

    /**
     * Enter animation
     */

    if (isPresent) {
      if (!unmounting) {
        onEntering();
        setStyles(
          ref.current,
          shouldSuppressAnimation ? target : resolveAnimationValues(enter)
        );
      }

      isUnmountAnimationPlayingRef.current = false;

      if (!shouldSuppressAnimation) {
        // @ts-expect-error FIXME(strictNullChecks)
        animate(
          ref.current,
          target,
          resolveOptions(sharedOptions, target)
        ).complete.then(onEntered);
      }
    } else {
      isUnmountAnimationPlayingRef.current = true;
      const exitAnimation = resolveAnimationValues(exit, custom);
      // @ts-expect-error FIXME(strictNullChecks)
      animate(
        ref.current,
        exitAnimation,
        resolveOptions(sharedOptions, exitAnimation)
      ).complete.then(() => {
        onExited();
        safeToRemove();
      });
    }
  });

  /**
   * Handle changes to the target animation definition
   * after the component has mounted.
   */
  useAnimation(ref, target, { enabled: isPresent, options: sharedOptions });
};
